import { useCallback, useEffect, useRef, useState } from 'react'
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  ScrollView,
  View,
} from 'react-native'
import ArrowOverlay from './ArrowOverlay'
import { gap as gapPadding } from '@ui/theme/values'

type ImageNavigationProps = {
  children: React.ReactNode
  currentIndex: number
  itemGap?: number
  itemWidth?: number
}

export default function ImageNavigation({
  children,
  currentIndex,
  itemGap = 32,
  itemWidth = 128,
}: ImageNavigationProps) {
  const scrollRef = useRef<ScrollView>(null)
  const [layoutWidth, setLayoutWidth] = useState(0)
  const [contentWidth, setContentWidth] = useState(0)
  const [scrollX, setScrollX] = useState(0)

  const hasOverflow = contentWidth > layoutWidth + itemWidth / 2
  const showLeft = hasOverflow && scrollX > 4
  const showRight =
    hasOverflow && scrollX + layoutWidth < contentWidth - itemWidth / 2

  const handleScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      setScrollX(event.nativeEvent.contentOffset.x)
    },
    []
  )

  const initialScroll = useRef(false)

  useEffect(() => {
    if (!scrollRef.current || !layoutWidth) return
    const targetX = Math.max(
      0,
      currentIndex * (itemWidth + itemGap) - layoutWidth / 2 + itemWidth / 2
    )
    scrollRef.current.scrollTo({ x: targetX, animated: initialScroll.current })
    initialScroll.current = true
  }, [currentIndex, layoutWidth, itemWidth, itemGap])

  const scrollBy = useCallback(
    (direction: 'left' | 'right') => {
      if (!scrollRef.current || !layoutWidth) return

      const scrollAmount = (itemWidth + itemGap) * 2
      const delta = (direction === 'left' ? -1 : 1) * scrollAmount
      const maxScroll = Math.max(0, contentWidth - layoutWidth)
      const nextX = Math.min(Math.max(scrollX + delta, 0), maxScroll)
      scrollRef.current.scrollTo({ x: nextX, animated: true })
    },
    [contentWidth, layoutWidth, scrollX, itemWidth, itemGap]
  )

  return (
    <View className="relative">
      <ScrollView
        ref={scrollRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        onContentSizeChange={(width) => setContentWidth(width)}
        onLayout={(event) => setLayoutWidth(event.nativeEvent.layout.width)}
      >
        <View className="flex-row items-center justify-center gap-0">
          <View style={{ width: gapPadding }} />
          <View className="flex-row" style={{ gap: itemGap }}>
            {children}
          </View>
          <View style={{ width: gapPadding }} />
        </View>
      </ScrollView>
      {Platform.OS === 'web' && (
        <>
          {showLeft && (
            <ArrowOverlay
              itemWidth={itemWidth}
              direction="left"
              onPress={() => scrollBy('left')}
            />
          )}
          {showRight && (
            <ArrowOverlay
              itemWidth={itemWidth}
              direction="right"
              onPress={() => scrollBy('right')}
            />
          )}
        </>
      )}
    </View>
  )
}
